/**
 * dialog-helper.js — Reusable accessible dialog controller
 *
 * Used by:
 *   - review-drawer.js (collection page preview drawer)
 *   - product-reviews.js (PDP review modal)
 *
 * Provides:
 *   - open/close with body scroll lock
 *   - focus trap (Tab/Shift+Tab wrap, recalculated on each Tab)
 *   - Escape key close
 *   - focus return to trigger element on close
 *   - AI widget hiding via body class
 *
 * Usage:
 *   var dialog = window.SmokeysDialog.create({
 *     overlay: document.getElementById('my-overlay'),
 *     container: document.getElementById('my-dialog'),
 *     openClass: 'open',
 *     bodyClass: 'perks-drawer-open', // optional
 *     onClose: function() { ... }     // optional callback
 *   });
 *
 *   dialog.open(triggerElement);
 *   dialog.close();
 *   dialog.isOpen();
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function create(opts) {
    var overlay = opts.overlay;
    var container = opts.container;
    var openClass = opts.openClass || 'open';
    var bodyClass = opts.bodyClass || null;
    var onClose = opts.onClose || null;
    var _trigger = null;
    var _open = false;

    // Close on overlay background click
    if (overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) close();
      });
    }

    // Keyboard: Escape + focus trap
    document.addEventListener('keydown', function (e) {
      if (!_open) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }

      if (e.key !== 'Tab' || !container) return;

      // Recalculate focusable elements every Tab press (content may have changed)
      var focusable = Array.from(container.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });

      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !container.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !container.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    function open(triggerEl) {
      if (_open) return;
      _trigger = triggerEl || document.activeElement;
      _open = true;

      if (overlay) overlay.classList.add(openClass);
      document.body.style.overflow = 'hidden';
      if (bodyClass) document.body.classList.add(bodyClass);

      // Focus first focusable element inside the dialog
      requestAnimationFrame(function () {
        if (!container) return;
        var firstFocusable = container.querySelector(FOCUSABLE);
        if (firstFocusable) firstFocusable.focus();
      });
    }

    function close() {
      if (!_open) return;
      _open = false;

      if (overlay) overlay.classList.remove(openClass);
      document.body.style.overflow = '';
      if (bodyClass) document.body.classList.remove(bodyClass);

      // Return focus to trigger
      if (_trigger) {
        try { _trigger.focus(); } catch (e) {}
        _trigger = null;
      }

      if (onClose) onClose();
    }

    function isOpen() { return _open; }

    return { open: open, close: close, isOpen: isOpen };
  }

  window.SmokeysDialog = { create: create };
})();
